import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../assets/styles/theme';
import { TextMain } from '../../../components/typography/Texts';
import RectangularButton from '../../../components/buttons/RectangularButton';
import { CemeteryCountDto } from '../../../services/Maintenance/maintenanceService.dto';
import { maintenanceService } from '../../../services/Maintenance/maintenanceService';
import FiltersPanel, { TableFilter } from '../../../components/table/FiltersPanel';
import { toCemeteryCountDto } from '../utils/maintenanceConverter';
import { useError } from '../../../providers/useError';
import { usePopup } from '../../../providers/PopupProvider';
import CemeteryPullPopover from './CemeteryPullPopover';

const Container = styled.div`
  padding: ${theme.paddingStandard}px;
  display: flex;
  justify-content: space-between;
`;

const GroupContainer = styled.div`
  padding: 0 ${theme.paddingStandard}px;
  display: flex;
  align-items: center;
  margin-left: 20px;
`;

const TextMainWithBorders = styled(TextMain)`
  border: 1px solid ${theme.color.lightBlack};
  border-radius: 3px;
  padding: 3px;
  margin: 5px;
`;

const Button = styled(RectangularButton)`
  font-weight: normal;
  height: 30px;
  margin: 5px 30px 5px 5px;
`;

interface Props {
  databaseMessages: number;
  onApply: (filters: Map<string, any>) => void;
}

const CemeteryHeader = (props: Props) => {
  const { t } = useTranslation('maintenance');
  const [cemeteryMessages, setCemeteryMessages] = useState<CemeteryCountDto>({ count: 0 });
  const { handleError } = useError();
  const { showPopover, hidePopover } = usePopup();

  useEffect(() => {
    maintenanceService
      .cemeteryMessagesCount()
      .then(response => setCemeteryMessages(response.data))
      .catch(e => handleError(e));
  }, []);

  const filterInitState = new Map([
    ['deleted', 'false'],
    ['requeued', 'false'],
  ]);

  const dropdownOptionsMap = new Map<string, string>([
    ['true', t('cemetery.yes')],
    ['false', t('cemetery.no')],
  ]);

  const filters: TableFilter[] = [
    {
      type: 'dropdown',
      name: 'deleted',
      label: t('cemetery.deleted-dropdown'),
      booleanMap: dropdownOptionsMap,
    },
    {
      type: 'dropdown',
      name: 'requeued',
      label: t('cemetery.requeued-dropdown'),
      booleanMap: dropdownOptionsMap,
    },
    {
      type: 'text',
      name: 'firstDeathSearch',
      label: t('cemetery.first-death-search'),
    },
    {
      type: 'text',
      name: 'lastDeathSearch',
      label: t('cemetery.last-death-search'),
    },
  ];

  const openPullPopup = (anchor: HTMLButtonElement) => {
    showPopover(<CemeteryPullPopover onContinuePressed={pullMessages} />, anchor);
  };

  const pullMessages = (count: number) => {
    maintenanceService.pullToDatabase(toCemeteryCountDto(count));
    hidePopover();
  };

  return (
    <Container>
      <GroupContainer>
        <TextMain>{t('cemetery.in-cemetery')}</TextMain>
        <TextMainWithBorders>{cemeteryMessages.count}</TextMainWithBorders>
        <Button label={t('cemetery.pull-btn')} onClick={openPullPopup} />
        <FiltersPanel onApply={props.onApply} filters={filters} filterInitState={filterInitState} />
      </GroupContainer>
      <GroupContainer>
        <TextMain>{t('cemetery.in-database')}</TextMain>
        <TextMainWithBorders>{props.databaseMessages}</TextMainWithBorders>
      </GroupContainer>
    </Container>
  );
};

export default CemeteryHeader;
